import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Plus, TrendingUp, TrendingDown, Calendar, CreditCard, Repeat } from "lucide-react";
import { Category, Conta, Transaction } from "@/types/database";
import { useAuth } from "@/hooks/useAuth";
import { getCategoryIcon } from "@/constants/categoryIcons";
import { format, addDays, addWeeks, addMonths, addYears } from 'date-fns';
import { ajustarDataValida, verificarAjusteData } from "@/lib/dateUtils";
import { QuickCategoryModal } from "./QuickCategoryModal";
import { SelectSeparator } from "@/components/ui/select";
import { logger } from "@/utils/logger";

interface TransactionFormProps {
  onSuccess: () => void;
  tipo: "entrada" | "saida";
  isCollapsed?: boolean;
  asDialog?: boolean;
  isOpen?: boolean;
  initialContaTipo?: "conta" | "cartao"; // Nova prop para forçar seleção de cartão
}

export function TransactionForm({
  onSuccess,
  tipo,
  isCollapsed = false,
  asDialog = true,
  isOpen: externalIsOpen,
  initialContaTipo
}: TransactionFormProps) {
  const [valor, setValor] = useState<number | string>("");
  const [descricao, setDescricao] = useState("");
  const [categoriaId, setCategoriaId] = useState("none");
  const [contaId, setContaId] = useState("");
  const [contaTipo, setContaTipo] = useState<"conta" | "carteira" | "cartao">("conta");
  const [isCompraParcelada, setIsCompraParcelada] = useState(false);
  const [numeroParcelas, setNumeroParcelas] = useState("1");
  const [data, setData] = useState(() => {
    const now = new Date();
    return format(now, 'yyyy-MM-dd');
  });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  
  // Determinar se o formulário está visível/aberto para carregar dados
  const isVisible = asDialog ? open : (externalIsOpen ?? true);

  const [categories, setCategories] = useState<Category[]>([]);
  const [accounts, setAccounts] = useState<Conta[]>([]);
  const [creditCards, setCreditCards] = useState<Conta[]>([]);
  const [previewParcelas, setPreviewParcelas] = useState<Array<{
    parcela: number;
    valor: number;
    dataVencimento: string;
  }>>([]);
  const [quickCategoryModalOpen, setQuickCategoryModalOpen] = useState(false);


  // Estados para transação recorrente
  const [isRecorrente, setIsRecorrente] = useState(false);
  const [frequenciaRepeticao, setFrequenciaRepeticao] = useState<"semanas" | "meses" | "anos">("meses");
  const [numeroTotalRepeticoes, setNumeroTotalRepeticoes] = useState(12);
  const [foiPaga, setFoiPaga] = useState<boolean>(() => {
    const dataAtual = new Date();
    const dataSelecionada = new Date(data);

    // Para receitas:
    if (tipo === "entrada") {
      // Se data futura → status inicial sempre PENDENTE (foi_paga = false)
      if (dataSelecionada > dataAtual) {
        return false;
      }
      // Se data passada → sistema sugere EFETUADA
      else if (dataSelecionada < dataAtual) {
        return true;
      }
      // Se data igual ao dia atual → usuário escolhe (default true)
      return true;
    }
    // Para despesas mantém o comportamento original
    return true;
  });

  const [dataPagamento, setDataPagamento] = useState(() => {
    const now = new Date();
    return format(now, 'yyyy-MM-dd');
  });

  const { user } = useAuth();

  // Efeito para ajustar "foiPaga" baseado no tipo de conta
  useEffect(() => {
    if (contaTipo === "cartao") {
      setFoiPaga(false);
      setIsRecorrente(false);
    }
  }, [contaTipo]);

  // Efeito para ajustar "foiPaga" e "dataPagamento" quando a data muda
  useEffect(() => {
    // Sincronizar data de pagamento com a data da transação por padrão
    // já que não existe campo específico para data de pagamento no formulário
    setDataPagamento(data);

    if (tipo === "entrada") {
      const dataAtual = new Date();
      const dataSelecionada = new Date(data);

      // Se data futura → status inicial sempre PENDENTE (foi_paga = false)
      if (dataSelecionada > dataAtual) {
        setFoiPaga(false);
      }
      // Se data passada → sistema sugere EFETUADA
      else if (dataSelecionada < dataAtual) {
        setFoiPaga(true);
      }
      // Para data atual mantém o valor atual (usuário escolhe)
    }
  }, [data, tipo]);

  // Função para gerar transações recorrentes
  const generateRecurringTransactions = async (parentTransaction: Transaction) => {
    try {
      logger.info('🔄 Iniciando geração de transações recorrentes', { 
        parentId: parentTransaction.id, 
        repeticoes: numeroTotalRepeticoes 
      });

      // Atualizar a transação pai com numeração apenas na descrição
      const { error: updateError } = await supabase
        .from("transacoes")
        .update({
          descricao: `${parentTransaction.descricao} (1/${numeroTotalRepeticoes})`,
          // Removidas as colunas que não existem no banco
          // numero_parcela: 1,
          // total_parcelas: numeroTotalRepeticoes
        })
        .eq('id', parentTransaction.id);

      if (updateError) {
        logger.error('❌ Erro ao atualizar transação pai (recorrência)', updateError, { parentId: parentTransaction.id });
        throw updateError;
      }

      logger.debug('✅ Transação pai atualizada com sucesso');

      // Gerar transações filhas
      const transactions = [parentTransaction];

      for (let i = 1; i < numeroTotalRepeticoes; i++) {
        let nextDate;
        const baseDate = new Date(parentTransaction.data);

        if (frequenciaRepeticao === "semanas") {
          nextDate = addWeeks(baseDate, i);
        } else if (frequenciaRepeticao === "meses") {
          nextDate = addMonths(baseDate, i);
        } else {
          nextDate = addYears(baseDate, i);
        }

        const formattedDate = format(nextDate, 'yyyy-MM-dd');

        const childTransaction = {
          user_id: parentTransaction.user_id,
          tipo: parentTransaction.tipo,
          valor: parentTransaction.valor,
          descricao: `${parentTransaction.descricao.split(' (')[0]} (${i + 1}/${numeroTotalRepeticoes})`,
          data: formattedDate,
          categoria_id: parentTransaction.categoria_id,
          conta_id: parentTransaction.conta_id,
          foi_paga: false,
          data_pagamento: null,
          transacao_pai_id: parentTransaction.id,
          // Removidas as colunas que não existem no banco
          // numero_parcela: i + 1,
          // total_parcelas: numeroTotalRepeticoes
        };

        // @ts-expect-error - Transaction type from database insert
        transactions.push(childTransaction);
      }

      // Inserir transações filhas
      const { error: insertError } = await supabase
        .from("transacoes")
        .insert(transactions.slice(1));

      if (insertError) {
        logger.error('❌ Erro ao inserir transações filhas (recorrência)', insertError);
        throw insertError;
      }

      logger.info(`✅ ${transactions.length - 1} transações recorrentes criadas com sucesso`);
      return transactions;
    } catch (error) {
      logger.error('❌ Erro em generateRecurringTransactions', error);
      throw error;
    }
  };

  // Função para gerar transações parceladas
  const generateParceledTransactions = async (parentTransaction: Transaction) => {
    try {
      logger.info('🔄 Iniciando geração de transações parceladas', { 
        parentId: parentTransaction.id, 
        parcelas: numeroParcelas 
      });

      const numParcelas = parseInt(numeroParcelas);
      const valorParcela = parentTransaction.valor / numParcelas;

      // 1) Atualizar a transação pai: descrição com (1/N) e valor unitário da parcela
      const { error: updateError } = await supabase
        .from("transacoes")
        .update({
          descricao: `${parentTransaction.descricao} (1/${numeroParcelas})`,
          valor: valorParcela,
          numero_parcela: 1,
          total_parcelas: numParcelas,
        })
        .eq('id', parentTransaction.id);

      if (updateError) {
        logger.error('❌ Erro ao atualizar transação pai (parcelamento)', updateError, { parentId: parentTransaction.id });
        throw updateError;
      }

      // Atualizar a data da transação pai para a data de fechamento
      // Isso será feito após calcular a data de fechamento correta

      // 2) Buscar informações do cartão (dia de fechamento)
      const { data: cartaoData, error: cartaoError } = await supabase
        .from("contas")
        .select("dia_fechamento")
        .eq("id", parentTransaction.conta_id)
        .single();

      if (cartaoError) {
        logger.error('❌ Erro ao buscar informações do cartão', cartaoError, { contaId: parentTransaction.conta_id });
        throw cartaoError;
      }

      const diaFechamento = cartaoData.dia_fechamento;

      // 3) Calcular datas das parcelas considerando o dia de fechamento
      const transactions = [parentTransaction];

      // Data base a partir do valor salvo na transação
      let dataString: string;
      if (typeof parentTransaction.data === 'string') {
        dataString = parentTransaction.data.includes('T')
          ? parentTransaction.data.split('T')[0]
          : parentTransaction.data;
      } else if (parentTransaction.data && typeof parentTransaction.data === 'object' && 'toISOString' in parentTransaction.data) {
        // @ts-expect-error - Date type narrowing
        dataString = parentTransaction.data.toISOString().split('T')[0];
      } else {
        logger.error('❌ Formato de data inválido na transação pai', null, { data: parentTransaction.data });
        throw new Error('Formato de data inválido na transação pai');
      }

      const [year, month, day] = dataString.split('-').map(Number);
      const baseDate = new Date(year, month - 1, day);
      if (isNaN(baseDate.getTime())) {
        logger.error('❌ Data base inválida', null, { year, month, day });
        throw new Error(`Data base inválida: ${year}-${month}-${day}`);
      }

      // Ajustar a data base para o fechamento da fatura atual ou próxima
      let adjustedBaseDate: Date;
      let ajusteAvisado = false;

      // ✅ Usar função segura para criar data de fechamento
      const dataFechamento = ajustarDataValida(year, month, diaFechamento);

      if (baseDate > dataFechamento) {
        // A compra foi após o fechamento, então a primeira parcela vai para o próximo mês
        logger.debug('Compra após fechamento, primeira parcela vai para o próximo mês');
        const proxMes = month === 12 ? 1 : month + 1;
        const proxAno = month === 12 ? year + 1 : year;
        adjustedBaseDate = ajustarDataValida(proxAno, proxMes, diaFechamento);

        // ✅ Verificar se houve ajuste e avisar usuário
        const { foiAjustado, mensagem } = verificarAjusteData(proxAno, proxMes, diaFechamento);
        if (foiAjustado && !ajusteAvisado) {
          logger.warn('⚠️ Data ajustada (fechamento)', { mensagem });
          toast.info(mensagem);
          ajusteAvisado = true;
        }
      } else {
        // A compra foi antes do fechamento, então a primeira parcela é no mês atual
        logger.debug('Compra antes do fechamento, primeira parcela no mês atual');
        adjustedBaseDate = dataFechamento;
      }

      // Atualizar a data da transação pai para a data de fechamento
      const formattedParentDate = format(adjustedBaseDate, 'yyyy-MM-dd');
      const { error: updateDateError } = await supabase
        .from("transacoes")
        .update({
          data: formattedParentDate
        })
        .eq('id', parentTransaction.id);

      if (updateDateError) {
        logger.error('❌ Erro ao atualizar data da transação pai', updateDateError);
        // Não bloquear o fluxo se falhar, apenas logar o erro
      } else {
        logger.debug('✅ Data da transação pai atualizada', { date: formattedParentDate });
        // Atualizar o objeto em memória também
        parentTransaction.data = formattedParentDate;
      }

      for (let i = 1; i < numParcelas; i++) {
        // Incrementar o mês para cada parcela subsequente
        const tempDate = addMonths(adjustedBaseDate, i);

        // ✅ Usar função segura para criar data da parcela
        const currentYear = tempDate.getFullYear();
        const currentMonth = tempDate.getMonth() + 1;
        const nextDate = ajustarDataValida(currentYear, currentMonth, adjustedBaseDate.getDate());

        if (!nextDate || isNaN(nextDate.getTime())) {
          logger.error('❌ nextDate inválida para parcela', null, { parcela: i + 1 });
          throw new Error(`Data de parcela inválida para parcela ${i + 1}`);
        }

        // Verificar ajuste apenas na primeira parcela após a inicial
        if (i === 1 && !ajusteAvisado) {
          const { foiAjustado } = verificarAjusteData(currentYear, currentMonth, adjustedBaseDate.getDate());
          if (foiAjustado) {
            toast.info(`Algumas parcelas foram ajustadas para o último dia do mês`);
            ajusteAvisado = true;
          }
        }

        const formattedDate = format(nextDate, 'yyyy-MM-dd');
        const childTransaction = {
          user_id: parentTransaction.user_id,
          tipo: parentTransaction.tipo,
          valor: valorParcela,
          descricao: `${parentTransaction.descricao.split(' (')[0]} (${i + 1}/${numParcelas})`,
          data: formattedDate,
          categoria_id: parentTransaction.categoria_id,
          conta_id: parentTransaction.conta_id,
          foi_paga: false,
          data_pagamento: null,
          transacao_pai_id: parentTransaction.id,
          numero_parcela: i + 1,
          total_parcelas: numParcelas,
        };
        // @ts-expect-error - Transaction type from database insert
        transactions.push(childTransaction);
      }

      // 3) Inserir parcelas filhas
      const { error: insertError } = await supabase
        .from("transacoes")
        .insert(transactions.slice(1));

      if (insertError) {
        logger.error('❌ Erro ao inserir parcelas filhas', insertError);
        throw insertError;
      }

      // 4) Recalcular faturas do cartão para refletir imediatamente as parcelas
      try {
        await supabase.rpc('recalcular_faturas_cartao', { p_cartao_id: parentTransaction.conta_id });
      } catch (e) {
        logger.warn('⚠️ Falha ao recalcular faturas (seguindo sem bloquear)', { error: e });
      }

      logger.info(`✅ ${transactions.length - 1} transações parceladas criadas com sucesso`);
      return transactions;
    } catch (error) {
      logger.error('❌ Erro em generateParceledTransactions', error);
      throw error;
    }
  };

  // Efeito para carregar contas e cartões
  useEffect(() => {
    // Simplificação radical: Sempre carregar dados se tiver usuário.
    // O custo de carregar dados "desnecessariamente" é menor do que o custo de não carregar quando precisa.
    // Especialmente porque o Supabase tem cache e é rápido.
    
    if (!user) return;

    const fetchData = async () => {
      console.log('TransactionForm Fetching Data (Forced)...');
      try {
        setLoading(true);

        // Buscar categorias
        const { data: categoriesData, error: categoriesError } = await supabase
          .from("categorias")
          .select("*")
          .eq("user_id", user.id)
          .or(`tipo.eq.${tipo},tipo.eq.ambos`)
          .order("nome");

        if (categoriesError) throw categoriesError;
        setCategories(categoriesData as Category[] || []);

        // Buscar contas
        const { data: accountsData, error: accountsError } = await supabase
          .from("contas")
          .select("*")
          .eq("user_id", user.id)
          .eq("ativo", true)
          .neq("tipo", "cartao_credito")
          .order("nome");

        if (accountsError) throw accountsError;
        console.log('Accounts loaded:', accountsData?.length);
        setAccounts(accountsData as Conta[] || []);

        // Buscar cartões
        const { data: cardsData, error: cardsError } = await supabase
          .from("contas")
          .select("*")
          .eq("user_id", user.id)
          .eq("ativo", true)
          .eq("tipo", "cartao_credito")
          .order("nome");

        if (cardsError) throw cardsError;
        setCreditCards(cardsData as Conta[] || []);

        // Se initialContaTipo for cartao e houver cartões, selecionar o primeiro automaticamente
        // Usamos uma flag para evitar que isso rode mais de uma vez
        // Verificamos se contaId está vazio para só setar na inicialização
        if (initialContaTipo === "cartao" && cardsData && cardsData.length > 0) {
          setContaId((current) => current || cardsData[0].id);
          setContaTipo("cartao");
        }

      } catch (error) {
        console.error("Erro ao carregar dados:", error);
        toast.error("Erro ao carregar dados para o formulário");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, tipo, initialContaTipo]); // Removido asDialog, externalIsOpen e open para simplificar

  // Efeito para atualizar o tipo de conta quando a conta é alterada
  useEffect(() => {
    if (!contaId) return;

    const selectedAccount = [...accounts, ...creditCards].find(acc => acc.id === contaId);
    if (selectedAccount) {
      if (selectedAccount.tipo === "cartao_credito") {
        setContaTipo("cartao");
        // Desabilitar recorrência para cartões
        setIsRecorrente(false);
        // Definir pagamento como não realizado para cartões
        setFoiPaga(false);
      } else if (selectedAccount.tipo === "carteira") {
        setContaTipo("carteira");
        // Habilitar pagamento para contas não-cartão
        setFoiPaga(true);
      } else {
        setContaTipo("conta");
        // Habilitar pagamento para contas não-cartão
        setFoiPaga(true);
      }
    }
  }, [contaId, accounts, creditCards]); // initialContaTipo removido pois é usado apenas na inicialização

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      logger.info('Tentativa de salvar transação', { tipo, categoriaId, contaId, isRecorrente, isCompraParcelada });

      const valorNumerico = Number(valor);

      // Verificar se é uma receita futura
      const dataAtual = new Date();
      const dataSelecionada = new Date(data);
      const isReceitaFutura = tipo === "entrada" && dataSelecionada > dataAtual;

      // Criar transação base
      const transactionData = {
        user_id: user?.id,
        tipo,
        valor: valorNumerico,
        descricao,
        data,
        categoria_id: categoriaId !== "none" ? categoriaId : null,
        conta_id: contaId,
        foi_paga: (isReceitaFutura || contaTipo === "cartao") ? false : foiPaga, // Forçar foi_paga=false para receitas futuras ou cartão
        data_pagamento: (isReceitaFutura || contaTipo === "cartao") ? null : (foiPaga ? dataPagamento : null)
      };

      const { data: newTransaction, error } = await supabase
        .from("transacoes")
        .insert(transactionData)
        .select()
        .single();

      if (error) {
        logger.error('Erro ao inserir transação base', error);
        throw error;
      }

      // Processar parcelamento ou recorrência
      if (isCompraParcelada && contaTipo === "cartao") {
        await generateParceledTransactions(newTransaction as unknown as Transaction);
      } else if (isRecorrente) {
        await generateRecurringTransactions(newTransaction as unknown as Transaction);
      }

      logger.info('Transação salva com sucesso', { id: newTransaction.id });

      const successMessage = `${tipo === "entrada" ? "Entrada" : "Despesa"} adicionada com sucesso!`;
      if (isRecorrente) {
        toast.success(`${successMessage} ${numeroTotalRepeticoes} repetições criadas.`);
      } else if (isCompraParcelada) {
        toast.success(`${successMessage} ${numeroParcelas} parcelas criadas.`);
      } else {
        toast.success(successMessage);
      }

      // Reset form
      setValor("");
      setDescricao("");
      setCategoriaId("none");
      setContaId("");
      setContaTipo("conta");
      setIsCompraParcelada(false);
      setNumeroParcelas("1");
      setIsRecorrente(false);
      setFrequenciaRepeticao("meses");
      setNumeroTotalRepeticoes(12);
      setFoiPaga(true);
      const now = new Date();
      setData(format(now, 'yyyy-MM-dd'));
      setOpen(false);
      onSuccess();
    } catch (error) {
      logger.error('Erro geral ao salvar transação', error);
      toast.error("Erro ao salvar transação");
    } finally {
      setLoading(false);
    }
  };

  const Icon = tipo === "entrada" ? TrendingUp : TrendingDown;
  const buttonText = tipo === "entrada" ? "Receita" : "Despesa";

  // Renderização do conteúdo do formulário
  const formContent = (
    <div className="flex flex-col gap-4 mt-2">
      {/* Descrição */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="descricao" className="text-sm font-medium">
          Descrição
        </Label>
        <Input
          id="descricao"
          type="text"
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          placeholder={tipo === "entrada" ? "Ex: Salário" : "Ex: Supermercado"}
          className="h-10 px-3 rounded-lg border bg-background"
          required
        />
      </div>

      {/* Valor */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="valor" className="text-sm font-medium">
          Valor (R$)
        </Label>
        <CurrencyInput
          id="valor"
          value={valor}
          onChange={setValor}
          placeholder="0,00"
          className="h-10 px-3 rounded-lg border bg-background text-left font-medium"
          required
        />
      </div>

      {/* Categoria */}
      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium">
          Categoria
        </Label>
        <Select
          value={categoriaId}
          onValueChange={(value) => {
            if (value === "add_new") {
              setQuickCategoryModalOpen(true);
            } else {
              setCategoriaId(value);
            }
          }}
        >
          <SelectTrigger aria-label="Selecionar categoria" className="h-10 px-3 rounded-lg border bg-background">
            <SelectValue placeholder="Selecionar categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Sem categoria</SelectItem>
            <SelectSeparator className="my-1" />
            <SelectItem
              value="add_new"
              className="text-blue-600 dark:text-blue-400 font-medium cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Adicionar Nova Categoria
              </div>
            </SelectItem>
            <SelectSeparator className="my-1" />
            {categories.map(category => {
              const Icon = getCategoryIcon(category.icone);
              return (
                <SelectItem key={category.id} value={category.id}>
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" style={{ color: category.cor }} />
                    {category.nome}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      <QuickCategoryModal
        open={quickCategoryModalOpen}
        onOpenChange={setQuickCategoryModalOpen}
        tipo={tipo}
        onSuccess={(newCategory) => {
          setCategories(prev => [...prev, newCategory]);
          setCategoriaId(newCategory.id);
          setQuickCategoryModalOpen(false);
        }}
      />

      {/* Data */}
      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium">
          Data
        </Label>
        <DatePicker
          value={data}
          onChange={setData}
          placeholder="Selecione a data"
          className="h-10 px-3 rounded-lg border bg-background w-full"
        />
      </div>

      {/* Conta */}
      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium">
          Conta
        </Label>
        <Select value={contaId} onValueChange={setContaId}>
          <SelectTrigger aria-label="Selecionar conta" className="h-10 px-3 rounded-lg border bg-background">
            <SelectValue placeholder="Selecionar conta" />
          </SelectTrigger>
          <SelectContent>
            {accounts.map(account => (
              <SelectItem key={account.id} value={account.id}>
                {account.nome}
              </SelectItem>
            ))}
            {tipo === "saida" && creditCards.length > 0 && (
              <SelectGroup>
                <SelectSeparator className="my-1" />
                <SelectLabel className="px-2 py-1.5 text-xs text-muted-foreground font-semibold">
                  Cartões de Crédito
                </SelectLabel>
                {creditCards.map(card => (
                  <SelectItem key={card.id} value={card.id}>
                    {card.nome}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Opções de parcelamento e recorrência */}
      {contaTipo === "cartao" && (
        <div className="flex items-center justify-between mt-1">
          <Label className="text-sm font-medium mb-0">
            Compra parcelada?
          </Label>
          <Switch
            checked={isCompraParcelada}
            onCheckedChange={setIsCompraParcelada}
            className="scale-90"
          />
        </div>
      )}

      {contaTipo !== "cartao" && (
        <div className="flex items-center justify-between mt-1">
          <Label className="text-sm font-medium mb-0">
            Transação recorrente?
          </Label>
          <Switch
            checked={isRecorrente}
            onCheckedChange={setIsRecorrente}
            className="scale-90"
          />
        </div>
      )}

      {/* Parcelamento - só aparece se for cartão e estiver marcado como parcelado */}
      {isCompraParcelada && contaTipo === "cartao" && (
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">
            Número de parcelas
          </Label>
          <Select value={numeroParcelas} onValueChange={setNumeroParcelas}>
            <SelectTrigger className="h-10 px-3 rounded-lg border bg-background">
              <SelectValue placeholder="Selecionar parcelas" />
            </SelectTrigger>
            <SelectContent>
              {[...Array(12)].map((_, i) => {
                const num = i + 1;
                // Cálculo seguro do valor da parcela
                const valorNum = typeof valor === 'string' 
                  ? parseFloat(valor.replace(/\./g, '').replace(',', '.') || '0') 
                  : Number(valor || 0);
                
                const valorParcela = valorNum ? (valorNum / num).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00';
                
                return (
                  <SelectItem key={num} value={num.toString()}>
                    {num}x de R$ {valorParcela}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Recorrência - só aparece se for recorrente */}
      {isRecorrente && (
        <div className="flex gap-3">
          <div className="flex-1 space-y-1.5">
            <Label className="text-sm font-medium">
              Frequência
            </Label>
            <Select value={frequenciaRepeticao} onValueChange={(value: "semanas" | "meses" | "anos") => setFrequenciaRepeticao(value)}>
              <SelectTrigger className="h-10 px-3 rounded-lg border bg-background">
                <SelectValue placeholder="Selecionar frequência" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="semanas">Semanal</SelectItem>
                <SelectItem value="meses">Mensal</SelectItem>
                <SelectItem value="anos">Anual</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 space-y-1.5">
            <Label className="text-sm font-medium">
              Repetições
            </Label>
            <Input
              type="number"
              min="2"
              max="60"
              value={numeroTotalRepeticoes}
              onChange={(e) => setNumeroTotalRepeticoes(parseInt(e.target.value))}
              className="h-10 px-3 rounded-lg border bg-background"
            />
          </div>
        </div>
      )}

      {/* Pagamento */}
      <div className="flex flex-col mt-1">
        <div className="flex items-center justify-between">
          <Label htmlFor="foi-paga-switch" className="text-sm font-medium mb-0">
            {tipo === "entrada" ? "Receita recebida?" : "Despesa paga?"}
          </Label>
          <Switch
            id="foi-paga-switch"
            checked={foiPaga}
            onCheckedChange={setFoiPaga}
            disabled={new Date(data) > new Date() || contaTipo === "cartao"}
            className="scale-90"
          />
        </div>
        
        {contaTipo === "cartao" && (
          <p className="text-xs text-muted-foreground text-center mt-1">
            Despesas no crédito são pagas através do fechamento da fatura
          </p>
        )}
      </div>
      
      {new Date(data) > new Date() && contaTipo !== "cartao" && (
        <p className="text-xs text-muted-foreground text-center mt-1">
          {tipo === "entrada" ? "Receitas futuras não podem ser marcadas como recebidas" : "Despesas futuras não podem ser marcadas como pagas"}
        </p>
      )}

      <div className="mt-4">
        <Button
          onClick={handleSubmit}
          className={`w-full h-11 rounded-lg text-white font-medium ${
            tipo === "entrada" 
              ? "bg-green-600 hover:bg-green-700" 
              : "bg-red-600 hover:bg-red-700"
          }`}
          disabled={loading || !valor || !descricao || !contaId}
        >
          {loading ? "Salvando..." : tipo === "entrada" ? "Salvar receita" : "Salvar despesa"}
        </Button>
      </div>
    </div>
  );

  if (!asDialog) {
    return formContent;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size={isCollapsed ? "icon" : "default"}
          className={`${isCollapsed ? "h-10 w-10 flex-shrink-0" : "w-full h-10 px-4 py-2"} ${isCollapsed ? "justify-center" : "justify-start gap-2"} transition-all duration-200 hover:shadow-medium ${tipo === "entrada" ? "bg-green-600 text-white border-green-600 hover:bg-green-700" : "bg-red-600 text-white border-red-600 hover:bg-red-700"
            }`}
          onClick={() => setOpen(true)}
        >
          {isCollapsed ? (
            <Icon className="h-5 w-5" />
          ) : (
            <>
              <Icon className="h-5 w-5" />
              <span>Nova {buttonText}</span>
            </>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5" />
            Nova {buttonText}
          </DialogTitle>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>
  );
}
